import React, { useState, useRef } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import RadioPillQuestion from '../../../core/compositions/radio_pill_question';
import CheckboxPillQuestion from '../../../core/compositions/checkbox_pill_question';
import QuestionWrapper from '../../../core/ui/question_wrapper';
import QuestionHeading from '../../../core/ui/question_heading';
import QuestionSubheading from '../../../core/ui/question_subheading';
import CoreTextInput from '../../../core/ui/text_input';
import ImportantSafetyInformationStickyFooter from '../../../isi/sticky_footer/index.jsx';
import BylvayIsi from '../../../isi/important_safety_information/bylvay/index.jsx';
import {
  QuestionnaireStepProvider,
  useQuestionnaireStep,
} from '../../../../wrappers/question_wrapper/step_context.jsx';

const RATING_LABELS = ['Very disappointing', 'Disappointing', 'Somewhat helpful', 'Helpful', 'Very helpful'];
const ratingOptions = RATING_LABELS.map((label, i) => ({ value: String(i + 1), label }));

const toOptions = (choices) => choices.map((c) => ({ value: c, label: c }));
const toggleInArray = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const AGREEMENT_CHOICES = ['Strongly agree', 'Agree', 'Neither agree nor disagree', 'Disagree', 'Strongly disagree'];

const buildSteps = (readyForNextStep, platformEase, hasHadTreatment, inspiredToShare) => {
  const steps = ['rating', 'comfort-level', 'confidence-level', 'connection-with-others', 'ready-for-next-step'];
  if (readyForNextStep && readyForNextStep !== 'Yes') {
    steps.push('not-ready-reasons');
  }
  steps.push('recommend-program', 'anything-else', 'platform-ease');
  if (platformEase && platformEase !== 'Very easy' && platformEase !== 'Easy') {
    steps.push('platform-improvements');
  }
  steps.push('inspired-to-share');
  const inspiredAgreed = inspiredToShare === 'Strongly agree' || inspiredToShare === 'Agree';
  if (hasHadTreatment && inspiredAgreed) {
    steps.push('interested-in-mentoring');
  }
  return steps;
};

const isiFooterProps = {
  initialHeight: 185,
  initialHeightMobile: 185,
  expandedHeight: 800,
};

function BylvayIsiFooter({ innerRef }) {
  return (
    <ImportantSafetyInformationStickyFooter component={<BylvayIsi showFooter />} {...isiFooterProps} ref={innerRef} />
  );
}

function TextInputStep({ heading, subHeading, value, onChange, placeholder, onSubmit, onBack, isDisabled }) {
  const { current, total } = useQuestionnaireStep();
  return (
    <QuestionWrapper current={current} total={total} onSubmit={onSubmit} onBack={onBack} isDisabled={isDisabled}>
      <QuestionHeading>{heading}</QuestionHeading>
      {subHeading && <QuestionSubheading>{subHeading}</QuestionSubheading>}
      <CoreTextInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        ariaLabel={placeholder}
        multiline
        minRows={4}
      />
    </QuestionWrapper>
  );
}

function Bylvay({ path, update, answeredFinalQuestion, completeSurvey, history, conversationSurvey }) {
  const [rating, setRating] = useState('');
  const [comfortLevel, setComfortLevel] = useState('');
  const [confidenceLevel, setConfidenceLevel] = useState('');
  const [connectionWithOthers, setConnectionWithOthers] = useState('');
  const [readyForNextStep, setReadyForNextStep] = useState('');
  const [notReadyReasons, setNotReadyReasons] = useState([]);
  const [notReadyReasonsOther, setNotReadyReasonsOther] = useState('');
  const [recommendProgram, setRecommendProgram] = useState('');
  const [anythingElse, setAnythingElse] = useState('');
  const [platformEase, setPlatformEase] = useState('');
  const [platformImprovements, setPlatformImprovements] = useState([]);
  const [inspiredToShare, setInspiredToShare] = useState('');
  const [interestedInMentoring, setInterestedInMentoring] = useState('');
  const isiRef = useRef(null);
  const hasHadTreatment = conversationSurvey.match_data?.has_had_treatment?.toLowerCase() === 'yes';

  return (
    <QuestionnaireStepProvider steps={buildSteps(readyForNextStep, platformEase, hasHadTreatment, inspiredToShare)}>
      <Switch>
        <Route exact path={`${path}`}>
          <Redirect to={`${path}/rating`} />
        </Route>

        {/* 1 - Star rating */}
        <Route exact path={`${path}/rating`}>
          <RadioPillQuestion
            heading={`Rate the conversation with your mentor ${conversationSurvey.patient_mentor_name}.`}
            subHeading='Your rating will not be shared directly with your mentor. It helps PatientPartner improve the program experience and support future connections.'
            name='rating'
            options={ratingOptions}
            value={rating}
            onChange={setRating}
            onSubmit={() => {
              const ratingValue = Number(rating);
              setRating(ratingValue);
              update({ rating: ratingValue });
              history.push(`${path}/comfort-level`);
            }}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 2 - Comfort level */}
        <Route exact path={`${path}/comfort-level`}>
          <RadioPillQuestion
            heading='My mentor made me (or someone I care for) feel more comfortable taking the next step in my treatment journey.'
            subHeading='Help us understand if your mentor eased your concerns.'
            name='comfort_level'
            options={toOptions(AGREEMENT_CHOICES)}
            value={comfortLevel}
            onChange={setComfortLevel}
            onSubmit={() => {
              update({ data: { comfort_level: comfortLevel } });
              history.push(`${path}/confidence-level`);
            }}
            onBack={() => history.push(`${path}/rating`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 3 - BYLVAY experience helpful */}
        <Route exact path={`${path}/confidence-level`}>
          <RadioPillQuestion
            heading='Hearing my mentor&rsquo;s experience with BYLVAY (odevixibat) was helpful.'
            subHeading='Reflecting on your conversation, please indicate how much you agree with the following statement.'
            name='confidence_level'
            options={toOptions(AGREEMENT_CHOICES)}
            value={confidenceLevel}
            onChange={setConfidenceLevel}
            onSubmit={() => {
              update({ data: { confidence_level: confidenceLevel } });
              history.push(`${path}/connection-with-others`);
            }}
            onBack={() => history.push(`${path}/comfort-level`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 4 - Connection with others */}
        <Route exact path={`${path}/connection-with-others`}>
          <RadioPillQuestion
            heading='I feel more connected to others managing this condition.'
            subHeading='Help us understand if the program made you feel less alone in your journey.'
            name='connection_with_others'
            options={toOptions(['True', 'False'])}
            value={connectionWithOthers}
            onChange={setConnectionWithOthers}
            onSubmit={() => {
              update({ data: { connection_with_others: connectionWithOthers } });
              history.push(`${path}/ready-for-next-step`);
            }}
            onBack={() => history.push(`${path}/confidence-level`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 5 - Plan to start/continue treatment */}
        <Route exact path={`${path}/ready-for-next-step`}>
          <RadioPillQuestion
            heading='Do you (or someone you care for) plan to start or continue treatment with BYLVAY (odevixibat)?'
            subHeading='Help us understand if you feel prepared to take the next step.'
            name='ready_for_next_step'
            options={toOptions(['Yes', 'Maybe', 'No'])}
            value={readyForNextStep}
            onChange={setReadyForNextStep}
            onSubmit={() => {
              update({ ready_for_next_step: readyForNextStep });
              if (readyForNextStep === 'Yes') {
                history.push(`${path}/recommend-program`);
              } else {
                history.push(`${path}/not-ready-reasons`);
              }
            }}
            onBack={() => history.push(`${path}/connection-with-others`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 6 - Why not start/continue (conditional: Maybe or No) */}
        <Route exact path={`${path}/not-ready-reasons`}>
          <CheckboxPillQuestion
            heading='Which best describes why you may not start or continue treatment? (Check all that apply)'
            subHeading="We'd love to better understand your situation."
            name='not_ready_reasons'
            options={toOptions([
              'I need more time to decide',
              'I still have questions for my doctor',
              'Concerns about possible side effects',
              'Insurance or cost concerns',
              'Waiting on additional testing',
              'Treatment was not recommended by my doctor',
              'Prefer not to say',
            ])}
            values={notReadyReasons}
            onToggle={(v) => setNotReadyReasons((prev) => toggleInArray(prev, v))}
            showOther
            otherValue={notReadyReasonsOther}
            onOtherChange={setNotReadyReasonsOther}
            otherPlaceholder='Other (please describe)'
            onSubmit={(values, otherValue) => {
              const result = [...(values || []), ...(otherValue && otherValue.trim() ? [otherValue.trim()] : [])];
              setNotReadyReasons(result);
              update({ data: { not_ready_reasons: result } });
              history.push(`${path}/recommend-program`);
            }}
            onBack={() => history.push(`${path}/ready-for-next-step`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 7 - Recommend program */}
        <Route exact path={`${path}/recommend-program`}>
          <RadioPillQuestion
            heading='I would recommend this mentor program to someone with or caring for someone with this condition.'
            subHeading=''
            name='recommend_program'
            options={toOptions(AGREEMENT_CHOICES)}
            value={recommendProgram}
            onChange={setRecommendProgram}
            onSubmit={() => {
              update({ data: { recommend_program: recommendProgram } });
              history.push(`${path}/anything-else`);
            }}
            onBack={() => {
              if (readyForNextStep === 'Yes') {
                history.push(`${path}/ready-for-next-step`);
              } else {
                history.push(`${path}/not-ready-reasons`);
              }
            }}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 8 - Thank you note (optional) */}
        <Route exact path={`${path}/anything-else`}>
          <TextInputStep
            heading='Would you like to share a note of thanks with your mentor? (Optional)'
            subHeading='Your message may be shared anonymously with your mentor. Please do not include medical details or treatment-related information'
            value={anythingElse}
            onChange={setAnythingElse}
            placeholder='Share appreciation, encouragement, or what stood out.'
            isDisabled={false}
            onSubmit={() => {
              update({ data: { anything_else: anythingElse } });
              history.push(`${path}/platform-ease`);
            }}
            onBack={() => history.push(`${path}/recommend-program`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 9 - Platform ease of use */}
        <Route exact path={`${path}/platform-ease`}>
          <RadioPillQuestion
            heading='How easy was it to use the PatientPartner platform?'
            subHeading='Help us improve the experience for future families.'
            name='platform_ease'
            options={toOptions(['Very easy', 'Easy', 'Neutral', 'Difficult', 'Very Difficult'])}
            value={platformEase}
            onChange={setPlatformEase}
            onSubmit={() => {
              update({ data: { platform_ease: platformEase } });
              if (platformEase === 'Very easy' || platformEase === 'Easy') {
                history.push(`${path}/inspired-to-share`);
              } else {
                history.push(`${path}/platform-improvements`);
              }
            }}
            onBack={() => history.push(`${path}/anything-else`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 10 - Platform improvements (conditional: Neutral/Difficult/Very Difficult) */}
        <Route exact path={`${path}/platform-improvements`}>
          <CheckboxPillQuestion
            heading='Is there anything we could improve about the platform experience? (Select all that apply)'
            subHeading=''
            name='platform_improvements'
            options={toOptions([
              'The platform was difficult to use',
              'I had trouble accessing or logging in',
              "Something didn't work as expected",
              'It was hard to find what I needed',
              "Pages were slow to load or didn't load properly",
              "The experience didn't work well on my device",
              'I received too many email or text notifications',
              'Getting started or signing up was difficult',
            ])}
            values={platformImprovements}
            onToggle={(v) => setPlatformImprovements((prev) => toggleInArray(prev, v))}
            onSubmit={(values) => {
              const result = values || [];
              setPlatformImprovements(result);
              update({ data: { platform_improvements: result } });
              history.push(`${path}/inspired-to-share`);
            }}
            onBack={() => history.push(`${path}/platform-ease`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 11 - Inspired to share */}
        <Route exact path={`${path}/inspired-to-share`}>
          <RadioPillQuestion
            heading='As a result of this interaction, I am inspired to share my story with others navigating this condition.'
            subHeading='Sharing your experience is completely voluntary.'
            name='inspired_to_share'
            options={toOptions(AGREEMENT_CHOICES)}
            value={inspiredToShare}
            onChange={setInspiredToShare}
            onSubmit={() => {
              update({ data: { inspired_to_share: inspiredToShare } });
              const isAgreed = inspiredToShare === 'Strongly agree' || inspiredToShare === 'Agree';
              if (hasHadTreatment && isAgreed) {
                history.push(`${path}/interested-in-mentoring`);
              } else {
                answeredFinalQuestion({ inspired_to_share: inspiredToShare });
              }
            }}
            onBack={() => {
              if (platformEase === 'Very easy' || platformEase === 'Easy') {
                history.push(`${path}/platform-ease`);
              } else {
                history.push(`${path}/platform-improvements`);
              }
            }}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>

        {/* 12 - Interested in becoming a mentor (conditional) */}
        <Route exact path={`${path}/interested-in-mentoring`}>
          <RadioPillQuestion
            heading='Would you be interested in becoming a PatientPartner Mentor to support others navigating this condition?'
            subHeading="You don't need to be an expert, just someone willing to share your personal experience in a supportive and respectful way."
            name='interested_in_mentoring'
            options={toOptions(["Yes, I'd like to learn more", 'Maybe in the future', 'No, not at this time'])}
            value={interestedInMentoring}
            onChange={setInterestedInMentoring}
            onSubmit={() => {
              if (!interestedInMentoring) return;
              answeredFinalQuestion({ data: { interested_in_mentoring: interestedInMentoring } });
            }}
            onBack={() => history.push(`${path}/inspired-to-share`)}
          />
          <BylvayIsiFooter innerRef={isiRef} />
        </Route>
      </Switch>
    </QuestionnaireStepProvider>
  );
}

export default Bylvay;
